import { useCallback, useEffect, useState } from 'react';
import apiAxios from 'helpers/apiAxios';

// Per-user preference stored server-side (GET/PUT /my/preferences/:key), so it
// follows the user across browsers. Keys are whitelisted by the backend
// (UserPreference::KEYS). `value` is null until something has been saved.
// `save` resolves with the server-normalised value and rethrows on failure so
// the caller can toast.
export const useUserPreference = (key, { enabled = true } = {}) => {
  const [value, setValue] = useState(null);
  const [loading, setLoading] = useState(enabled);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    setLoading(true);
    apiAxios
      .get(`/my/preferences/${key}`)
      .then((res) => { if (!cancelled) setValue(res.data?.data?.value ?? null); })
      .catch(() => { /* a missing preference must never block the form */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [key, enabled]);

  const save = useCallback(async (next) => {
    setSaving(true);
    try {
      const res = await apiAxios.put(`/my/preferences/${key}`, { value: next });
      const saved = res.data?.data?.value ?? null;
      setValue(saved);
      return saved;
    } finally {
      setSaving(false);
    }
  }, [key]);

  return { value, loading, saving, save };
};

export default useUserPreference;
